import React, { useMemo } from 'react'
import classNames from 'classnames'

import useDialog from '../../hooks/dialog/useDialog'
import ReactionsDialog from '../dialogs/ReactionsDialog'
import {
  HoverInfoTrigger,
  PeopleHoverCard,
  useContactsById,
  type PersonRow,
} from '../PeopleHoverInfo'

import styles from './styles.module.scss'

import type { T } from '@deltachat/jsonrpc-client'
import useTranslationFunction from '../../hooks/useTranslationFunction'

// Reactions are sorted by their frequencies in the core, that is, the
// most used emojis come first in this list.

type Props = {
  reactions: T.Reactions
  tabindexForInteractiveContents: -1 | 0
  messageWidth: number
}

export default function Reactions(props: Props) {
  const tx = useTranslationFunction()

  const { messageWidth } = props

  const { openDialog } = useDialog()
  const { reactionsByContact, reactions } = props.reactions

  // Compute visibleEmojis and hiddenReactionsCount from props
  const { visibleEmojis, hiddenReactionsCount } = useMemo(() => {
    let emojiSpaces = 0
    if (messageWidth <= 234) {
      emojiSpaces = 1
    } else {
      emojiSpaces = Math.round((messageWidth - 200) / 34)
    }
    const totalReactionsCount = reactions.reduce(
      (sum, item) => sum + item.count,
      0
    )
    const visibleReactionsCount = reactions
      .slice(0, emojiSpaces)
      .reduce((sum, item) => sum + item.count, 0)
    if (reactions.length - emojiSpaces <= 1) {
      emojiSpaces++
      return { visibleEmojis: emojiSpaces, hiddenReactionsCount: 0 }
    } else {
      return {
        visibleEmojis: emojiSpaces,
        hiddenReactionsCount: totalReactionsCount - visibleReactionsCount,
      }
    }
  }, [messageWidth, reactions])

  const handleClick = () => {
    openDialog(ReactionsDialog, {
      reactionsByContact,
    })
  }

  // Emojis that don't fit and are collapsed into the "+N" pill.
  const hiddenEmojis = reactions.slice(visibleEmojis).map(r => r.emoji)

  return (
    <div className={styles.reactions}>
      {reactions.slice(0, visibleEmojis).map(({ emoji, isFromSelf, count }) => {
        return (
          <HoverInfoTrigger
            key={emoji}
            align='start'
            className={styles.emojiTrigger}
            onClick={handleClick}
            card={
              <ReactorsHoverCard
                reactionsByContact={reactionsByContact}
                emojis={[emoji]}
                showEmoji={false}
              />
            }
          >
            <span
              className={classNames(styles.emoji, {
                [styles.isFromSelf]: isFromSelf,
              })}
            >
              {emoji}
              {count > 1 && <span className={styles.emojiCount}>{count}</span>}
            </span>
          </HoverInfoTrigger>
        )
      })}
      {reactions.length > visibleEmojis && (
        <HoverInfoTrigger
          align='start'
          className={styles.emojiTrigger}
          onClick={handleClick}
          card={
            <ReactorsHoverCard
              reactionsByContact={reactionsByContact}
              emojis={hiddenEmojis}
              title={tx('reactions')}
              showEmoji={true}
            />
          }
        >
          <span className={classNames(styles.emoji, styles.emojiCount)}>
            +{hiddenReactionsCount}
          </span>
        </HoverInfoTrigger>
      )}
      <button
        type='button'
        className={styles.openReactionsListDialogButton}
        aria-label={tx('more_info_desktop')}
        onClick={handleClick}
        tabIndex={props.tabindexForInteractiveContents}
      ></button>
    </div>
  )
}

/**
 * Popover contents for a single reaction (or the "+N" overflow pill): the
 * people who reacted with any of `emojis`, each with their avatar and name.
 * When hovering one emoji we show neither a title nor a per-row emoji — the
 * user already knows which emoji they're pointing at. The overflow pill mixes
 * several emojis, so it gets a "Reactions" title and shows each reactor's
 * emoji. Reactors follow the core's most-used-first ordering.
 */
function ReactorsHoverCard(props: {
  reactionsByContact: T.Reactions['reactionsByContact']
  emojis: string[]
  title?: React.ReactNode
  showEmoji: boolean
}) {
  const tx = useTranslationFunction()
  const { reactionsByContact, emojis, showEmoji } = props

  const reactors = useMemo(() => {
    const entries: { contactId: number; emoji: string }[] = []
    for (const emoji of emojis) {
      for (const [contactId, contactEmojis] of Object.entries(
        reactionsByContact
      )) {
        // The dialog and the bubble only ever show a contact's first reaction,
        // so we mirror that here.
        if (contactEmojis[0] === emoji) {
          entries.push({ contactId: Number(contactId), emoji })
        }
      }
    }
    return entries
  }, [emojis, reactionsByContact])

  const contacts = useContactsById(reactors.map(r => r.contactId))

  const rows: PersonRow[] = reactors
    .map(({ contactId, emoji }): PersonRow | null => {
      const contact = contacts[contactId]
      if (!contact) {
        return null
      }
      return {
        key: contactId,
        contact,
        detail: showEmoji ? (
          <span className={styles.hoverEmoji}>{emoji}</span>
        ) : undefined,
        detailTitle: showEmoji ? tx('reacted_with', emoji) : undefined,
      }
    })
    .filter((row): row is PersonRow => row !== null)

  const loading = rows.length === 0 && reactors.length > 0

  return <PeopleHoverCard title={props.title} rows={rows} loading={loading} />
}
